/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  FARISTAA — Centralized Appointment Status Color System
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * HOW TO ADD A NEW STATUS:
 *   1. Add a key here with all 5 fields (hex, bg, text, border, icon)
 *   2. That's it — every component that uses these helpers auto-updates.
 *
 * COLOR ANATOMY (per status):
 *   hex    — pure color for charts, dots, active card fill
 *   bg     — very light tint (~8% opacity) for card backgrounds
 *   text   — dark shade (~80%) for readable label text
 *   border — medium tint (~25%) for card borders
 *   icon   — medium-dark shade for icons inside cards
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const STATUS_COLORS = {
  all: {
    hex:    '#3B82F6',   // Blue-500
    bg:     '#EFF6FF',   // Blue-50
    text:   '#1D4ED8',   // Blue-700
    border: '#BFDBFE',   // Blue-200
    icon:   '#2563EB',   // Blue-600
    label:  'All',
    faIcon: 'fa-calendar-day',
  },
  pending: {
    hex:    '#F59E0B',   // Amber-400
    bg:     '#FFFBEB',   // Amber-50
    text:   '#92400E',   // Amber-800
    border: '#FDE68A',   // Amber-200
    icon:   '#D97706',   // Amber-600
    label:  'Pending',
    faIcon: 'fa-clock',
  },
  confirmed: {
    hex:    '#6366F1',   // Indigo-500
    bg:     '#EEF2FF',   // Indigo-50
    text:   '#3730A3',   // Indigo-800
    border: '#C7D2FE',   // Indigo-200
    icon:   '#4F46E5',   // Indigo-600
    label:  'Confirmed',
    faIcon: 'fa-calendar-check',
  },
  'checked-in': {
    hex:    '#0D9488',   // Teal-600 (app primary)
    bg:     '#F0FDFA',   // Teal-50
    text:   '#134E4A',   // Teal-900
    border: '#99F6E4',   // Teal-200
    icon:   '#0D9488',   // Teal-600
    label:  'In Clinic',
    faIcon: 'fa-hospital-user',
  },
  completed: {
    hex:    '#22C55E',   // Green-500
    bg:     '#F0FDF4',   // Green-50
    text:   '#14532D',   // Green-900
    border: '#BBF7D0',   // Green-200
    icon:   '#16A34A',   // Green-600
    label:  'Done',
    faIcon: 'fa-circle-check',
  },
  cancelled: {
    hex:    '#EF4444',   // Red-500
    bg:     '#FEF2F2',   // Red-50
    text:   '#991B1B',   // Red-800
    border: '#FECACA',   // Red-200
    icon:   '#DC2626',   // Red-600
    label:  'Cancelled',
    faIcon: 'fa-ban',
  },
  // ── Extra filter cards (type / mode) ────────────────────────
  online: {
    hex:    '#7C3AED',
    bg:     '#F5F3FF',
    text:   '#4C1D95',
    border: '#DDD6FE',
    icon:   '#7C3AED',
    label:  'Online',
    faIcon: 'fa-wifi',
  },
  offline: {
    hex:    '#F97316',
    bg:     '#FFF7ED',
    text:   '#9A3412',
    border: '#FED7AA',
    icon:   '#EA580C',
    label:  'Walk-in',
    faIcon: 'fa-person-walking',
  },
  video: {
    hex:    '#0EA5E9',
    bg:     '#F0F9FF',
    text:   '#075985',
    border: '#BAE6FD',
    icon:   '#0284C7',
    label:  'Video',
    faIcon: 'fa-video',
  },
  clinic: {
    hex:    '#0D9488',
    bg:     '#F0FDFA',
    text:   '#134E4A',
    border: '#99F6E4',
    icon:   '#0F766E',
    label:  'Clinic',
    faIcon: 'fa-hospital',
  },
};

// ── Helper: card background ────────────────────────────────────────────────
export const cardBg     = (status) => STATUS_COLORS[status]?.bg     ?? '#F8FAFC';

// ── Helper: icon color ─────────────────────────────────────────────────────
export const iconColor  = (status) => STATUS_COLORS[status]?.icon   ?? '#64748B';

// ── Helper: chart / donut segment color ───────────────────────────────────
export const chartColor = (status) => STATUS_COLORS[status]?.hex    ?? '#94A3B8';

// ── Helper: badge inline styles ───────────────────────────────────────────
export const badgeStyle = (status) => ({
  background: STATUS_COLORS[status]?.bg   ?? '#F1F5F9',
  color:      STATUS_COLORS[status]?.text ?? '#475569',
  border:     `1px solid ${STATUS_COLORS[status]?.border ?? '#E2E8F0'}`,
});

// ── Helper: card border color ─────────────────────────────────────────────
export const borderColor = (status) => STATUS_COLORS[status]?.border ?? '#E2E8F0';

// ── Helper: active card style (when selected/filtered) ────────────────────
export const activeCardStyle = (status) => ({
  background:   STATUS_COLORS[status]?.hex  ?? '#3B82F6',
  borderColor:  STATUS_COLORS[status]?.hex  ?? '#3B82F6',
  color:        '#ffffff',
});

// ── Helper: inactive card style ───────────────────────────────────────────
export const inactiveCardStyle = (status) => ({
  background:  STATUS_COLORS[status]?.bg     ?? '#F8FAFC',
  borderColor: STATUS_COLORS[status]?.border ?? '#E2E8F0',
  color:       STATUS_COLORS[status]?.text   ?? '#475569',
});

// ── Helper: row card background (appointment list rows) ───────────────────
export const rowBg = (status) => {
  const map = {
    pending:      `${STATUS_COLORS.pending['bg']}`,
    confirmed:    `${STATUS_COLORS.confirmed['bg']}`,
    'checked-in': `${STATUS_COLORS['checked-in']['bg']}`,
    completed:    `${STATUS_COLORS.completed['bg']}`,
    cancelled:    `${STATUS_COLORS.cancelled['bg']}`,
  };
  return map[status] ?? '#FFFFFF';
};

// ── Helper: row border color ───────────────────────────────────────────────
export const rowBorder = (status) => STATUS_COLORS[status]?.border ?? '#E2E8F0';
